/**
 * Whether a backend-authored link is safe to place in an `href` or `src`.
 *
 * The backend applies the same rule on write, so a link that fails here cannot
 * normally be stored. The surfaces check anyway, and deliberately: a preview
 * renders form values the backend has never seen, and a compromised or
 * mis-migrated row would otherwise reach an attribute that executes it.
 *
 * Canonical home. It lives in this lib rather than in either app because both
 * render announcement content and an app may not import another app's source —
 * every copy of a rule like this is one that can silently drift out of step.
 */

/** An absolute `http(s)://` destination. */
export function isAbsoluteHttpLink(link: string): boolean {
  return /^https?:\/\//i.test(link)
}

const SAME_ORIGIN_BASE = 'https://app.gtowizard.invalid'

/**
 * A leading-slash path that remains on the fixed base origin after browser URL
 * normalization. Parsing is essential: backslashes and ASCII control
 * characters can normalize a value that looks path-relative into `//evil.com`.
 */
export function isInAppPath(link: string): boolean {
  if (!link.startsWith('/')) {
    return false
  }

  try {
    return new URL(link, SAME_ORIGIN_BASE).origin === SAME_ORIGIN_BASE
  } catch {
    return false
  }
}

/**
 * Everything else — `javascript:`, `data:`, protocol-relative hosts, bare
 * relative paths, the empty string — is rejected, and the caller renders no
 * link at all rather than a broken or hostile one.
 */
export function isSafeAnnouncementLink(link: string): boolean {
  return isAbsoluteHttpLink(link) || isInAppPath(link)
}

/**
 * A blob URL is safe in an image or video `src`, which cannot execute what it
 * loads, and hostile in an `href`: a blob document inherits the origin that
 * created it, so navigating to one would run its script as the admin. Only the
 * media branches of the block gate may consult this, never the CTA branch.
 *
 * The admin preview needs it because a file the operator has picked, and which
 * has not reached the bucket yet, has no other address.
 */
export function isStagedMediaUrl(link: string): boolean {
  return link.startsWith('blob:')
}

type LinkActivation = {
  altKey: boolean
  button: number
  ctrlKey: boolean
  metaKey: boolean
  shiftKey: boolean
}

/** Whether an anchor activation should be upgraded to client-side navigation. */
export function isPlainLeftClick(event: LinkActivation): boolean {
  return event.button === 0 && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey
}
